/**
 * Utility functions for determining RMS monitoring status
 */

import { DateTime, TimeZone, TimeUnit } from "timezonecomplete"

export type RmsStatus = "Initiating" | "Healthy" | "No data" | null

export interface RmsStatusInput {
  assignmentStatus: "active" | "inactive" | string | null | undefined
  assignmentStart: string | null | undefined
  hasRecentData: boolean
  monitorKind: string | null | undefined
}

/**
 * Determines the RMS status based on assignment status, timing, and data availability
 *
 * Logic:
 * - Only applies to RMS monitors when assignment is active
 * - "Initiating": active assignment + no data + assignment started < 5 minutes ago
 * - "Healthy": active assignment + has recent data (within 3 minutes)
 * - "No data": active assignment + no data + assignment started > 5 minutes ago
 * - null: not applicable (inactive, non-RMS, etc.)
 */
export function determineRmsStatus({
  assignmentStatus,
  assignmentStart,
  hasRecentData,
  monitorKind,
}: RmsStatusInput): RmsStatus {
  // Only show status for RMS monitors when monitoring is active
  if (monitorKind !== "RMS" || assignmentStatus !== "active") {
    return null
  }

  if (!assignmentStart) {
    return null // Cannot determine 'Initiating' or 'No data' without start time
  }

  const now = new DateTime()

  // Parse assignment start time - if no timezone info, assume UTC
  let startTime: DateTime
  try {
    startTime = new DateTime(assignmentStart, TimeZone.utc())
  } catch (error) {
    // Invalid date string - cannot determine status
    console.warn(`Invalid assignment start date: ${assignmentStart}`)
    return null
  }

  if (hasRecentData) {
    return "Healthy"
  }

  // No recent data - check if assignment started within the past 5 minutes
  const fiveMinutesAgo = now.sub(5, TimeUnit.Minute)

  if (startTime.unixUtcMillis() >= fiveMinutesAgo.unixUtcMillis()) {
    return "Initiating"
  } else {
    return "No data"
  }
}

/**
 * Checks if IoT data response indicates recent data availability
 */
export function hasRecentIoTData(dataResponse: any): boolean {
  if (!dataResponse?.values || !Array.isArray(dataResponse.values)) {
    return false
  }

  // Check if we have any data points with non-null values
  return dataResponse.values.some((dataPoint: any) => dataPoint?.value !== null && dataPoint?.value !== undefined)
}
